import { ReactNode } from 'react'

export interface ContentBlock {
  type: 'p' | 'h2' | 'h3' | 'ul' | 'ol' | 'table' | 'callout' | 'code'
  text?: string
  items?: string[]
  headers?: string[]
  rows?: string[][]
  variant?: 'info' | 'warning' | 'success' | 'error'
  title?: string
  language?: string
}

interface ContentRendererProps {
  content: ContentBlock[]
}

// Generate URL-friendly ID from text
function generateId(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
}

// Parse inline formatting: **bold** and [links](url)
function parseInlineContent(text: string): ReactNode {
  const parts: ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Check for bold **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    // Check for links [text](url)
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/)

    // Find which comes first
    const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : -1
    const linkIndex = linkMatch ? remaining.indexOf(linkMatch[0]) : -1

    if (boldIndex === -1 && linkIndex === -1) {
      // No more formatting, add remaining text
      parts.push(remaining)
      break
    }

    // Process whichever comes first
    if (boldIndex !== -1 && (linkIndex === -1 || boldIndex < linkIndex)) {
      // Bold comes first
      if (boldIndex > 0) {
        parts.push(remaining.substring(0, boldIndex))
      }
      parts.push(<strong key={key++}>{boldMatch![1]}</strong>)
      remaining = remaining.substring(boldIndex + boldMatch![0].length)
    } else {
      // Link comes first
      if (linkIndex > 0) {
        parts.push(remaining.substring(0, linkIndex))
      }
      parts.push(
        <a
          key={key++}
          href={linkMatch![2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#FC87B7] hover:text-[#FDA4C7] underline underline-offset-2"
        >
          {linkMatch![1]}
        </a>
      )
      remaining = remaining.substring(linkIndex + linkMatch![0].length)
    }
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>
}

export function ContentRenderer({ content }: ContentRendererProps) {
  return (
    <>
      {content.map((block, index) => {
        switch (block.type) {
          case 'p':
            return <p key={index}>{parseInlineContent(block.text || '')}</p>

          case 'h2':
            const h2Id = generateId(block.text || '')
            return (
              <h2 key={index} id={h2Id}>
                {block.text}
              </h2>
            )

          case 'h3':
            const h3Id = generateId(block.text || '')
            return (
              <h3 key={index} id={h3Id}>
                {block.text}
              </h3>
            )

          case 'ul':
            return (
              <ul key={index}>
                {block.items?.map((item, i) => (
                  <li key={i}>{parseInlineContent(item)}</li>
                ))}
              </ul>
            )

          case 'ol':
            return (
              <ol key={index}>
                {block.items?.map((item, i) => (
                  <li key={i}>{parseInlineContent(item)}</li>
                ))}
              </ol>
            )

          case 'table':
            return (
              <div key={index} className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      {block.headers?.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows?.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{parseInlineContent(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )

          case 'callout':
            return (
              <div
                key={index}
                className={`callout callout-${block.variant || 'info'}`}
              >
                {block.title && (
                  <div className="callout-title">
                    <span>{block.title}</span>
                  </div>
                )}
                <div className="callout-content">
                  {parseInlineContent(block.text || '')}
                </div>
              </div>
            )

          case 'code':
            return (
              <pre key={index} className={block.language ? `language-${block.language}` : ''}>
                <code>{block.text}</code>
              </pre>
            )

          default:
            return null
        }
      })}
    </>
  )
}
